import { CanActivateFn, Router } from '@angular/router';
import { IframeCommunication } from '../../features/configurator/service/iframe-communication';
import { inject } from '@angular/core';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  throwError,
  forkJoin,
  shareReplay,
  from,
  tap,
} from 'rxjs';
import { VCApiService } from '../../features/configurator/service/vc-api';
import { VcDataService } from '../../features/configurator/service/vc-data';
import { EMPTY_GUID } from '../../shared/constants/global';
import { GlobalService } from '../../shared/services/global.service';
import { LoginService } from '../services/login.service';
import {
  OPERATION_TYPE,
  ORDER_STATUS,
} from '../../features/configurator/vas-configurator.constants';
import { LoaderService } from '../../shared/loader/loader.service';
import { ModalService } from '../../shared/modals/modal.service';

export const configuratorGuard: CanActivateFn = (route, state) => {
  const commService = inject(IframeCommunication);
  const router = inject(Router);
  const apiService = inject(VCApiService);
  const vcDataService = inject(VcDataService);
  const globalService = inject(GlobalService);
  const loginService = inject(LoginService);
  const loader = inject(LoaderService);
  const modalService = inject(ModalService);
  if (vcDataService.vcFgDetails()?.FinishedGoodId || vcDataService.vcFgDetails()?.PartNumber) {
    return true;
  }

  let isIframe = false;
  try {
    isIframe = window.self !== window.top;
  } catch {
    isIframe = true;
  }

  if (!isIframe) {
    return false;
  }

  commService.init();
  const requestSource$ = commService.data$.pipe(
    filter((res) => !!res),
    take(1),
  );

  return requestSource$.pipe(
    switchMap((req) => {
      loader.setLoadingState.set(true);
      console.log('Iframe - configurator payload', req);
      const requestHeader: any = req.requestHeader;
      let finishedGood: any =
        requestHeader.operationType === OPERATION_TYPE.Add ? null : req.finishedGood;

      if (
        requestHeader.operationType !== OPERATION_TYPE.Add &&
        requestHeader.operationType !== OPERATION_TYPE.Edit
      ) {
        const epl = vcDataService.getResponseHeader(
          'ERROR',
          requestHeader,
          'Operation Type is not Valid',
        );
        commService.sendToHost({ type: 'VAS_ERROR', payload: epl });
        loader.setLoadingState.set(false);
        return throwError(() => new Error('Unexpected Error'));
      }

      if (requestHeader.operationType === OPERATION_TYPE.Add) {
        vcDataService.setAddFgOperationType(true);
      } else if (!finishedGood) {
        const epl = vcDataService.getResponseHeader(
          'ERROR',
          requestHeader,
          'Finished Good Data not found in the request',
        );
        commService.sendToHost({ type: 'VAS_ERROR', payload: epl });
        loader.setLoadingState.set(false);

        return throwError(() => new Error('Unexpected Error'));
      }

      if (requestHeader.salesOrderStatus === ORDER_STATUS.Submitted) {
        vcDataService.setOrderStatus(true);
      }

      vcDataService.setHostRequest(requestHeader);
      if (!vcDataService.isAddFgMode()) {
        vcDataService.setHostFgPayload(finishedGood);
      }
      globalService.setDataToStorage('BusinessUnit-Name', requestHeader.businessUnit, true);

      return loginService.getBusinessUnits().pipe(
        switchMap((result) => {
          let buId = null;
          if (result?.Result?.length) {
            buId = result.Result.find(
              (bu: any) => bu.BusinessUnitName === requestHeader.businessUnit,
            )?.BusinessUnitId;
          } else {
            const epl = vcDataService.getResponseHeader(
              'ERROR',
              requestHeader,
              'Business Unit is not Valid',
            );
            commService.sendToHost({ type: 'VAS_ERROR', payload: epl });
            loader.setLoadingState.set(false);

            return throwError(() => new Error('Unexpected Error'));
          }
          localStorage.removeItem('BusinessUnit-Name');
          globalService.setDataToStorage('Business-Unit-Id', buId, true);
          globalService.setDataToStorage('Email-Id', requestHeader.loginUserId, true);
          globalService.setDataToStorage(
            'Sales-Location-Code',
            requestHeader.salesLocationId,
            true,
          );

          // const customers$ = globalService.getCustomerListData()?.length
          //   ? of(globalService.getCustomerListData())
          //   : globalService.getCustomers();

          const userAndLookup$ = loginService.getUserDetails().pipe(
            switchMap((user) => {
              loginService.storeUserDetails(user.Result);
              loginService.resetLookupDataState();
              return loginService.fetchAndCacheLookupData();
            }),
          );
          const payload = [
            {
              LookupType: 'Customer',
              EntityId: null,
              EntityCode: requestHeader.customerAccountNumber,
            },
            {
              LookupType: 'ShipToCustomer',
              EntityId: null,
              EntityCode: !vcDataService.isAddFgMode() ? finishedGood.componentShipTo : null,
            },
          ];
          const sharedLookup$ = apiService.fetchLookupData(payload).pipe(shareReplay(1));
          const customers$ = sharedLookup$.pipe(
            map((response) => response?.Customers || []),
            tap((data) => globalService.setCustomerListData(data)),
            take(1),
          );

          const shipToAndProcessSite$ = sharedLookup$.pipe(
            tap((response) => {
              const obj = {
                attributeList: response?.ShipToCustomers || [],
              };
              vcDataService.setShipTo(obj);
            }),
            take(1),
          );
          // const supplierShipToAndProcessSite$ =
          // globalService.getSupplierSiteAndShip()?.siteList?.length
          //   ? of(globalService.getSupplierSiteAndShip())
          //   : globalService.getSiteAndShipTo();

          const supplierShipToAndProcessSite$ = from(
            globalService.lookupDataListCategories(['includeSupplierSiteList']),
          ).pipe(
            take(1),
            tap((res) => {
              const obj = {
                siteList: res?.SupplierSites || [],
              };
              vcDataService.setSupplierSiteAndShip(obj);
            }),
          );
          const pl = {
            TransactiblePartNumber: vcDataService.isAddFgMode()
              ? requestHeader.fgTransactablePartNumber
              : finishedGood.transactablePartNumber,
            SalesLocationNumber: requestHeader.salesLocationId ?? '',
          };
          const fgDetails$ = apiService.getConfiguratorFinishedGoodDetails(pl).pipe(take(1));

          return forkJoin([
            userAndLookup$,
            customers$,
            shipToAndProcessSite$,
            supplierShipToAndProcessSite$,
            fgDetails$,
          ]).pipe(
            map(([, , , , searchResponse]: [any, any, any, any, any]) => {
              if (searchResponse) {
                const fgId = searchResponse.FinishedGoodId;
                if (fgId === EMPTY_GUID) {
                  errorDialog(requestHeader, 'vc.fgnf', modalService, vcDataService, commService);
                  loader.setLoadingState.set(false);

                  return false;
                }

                if (
                  requestHeader.sourceApplicationID === 'SIR' &&
                  searchResponse.BOMType === 'PriceBundle'
                ) {
                  errorDialog(requestHeader, 'vc.spbe', modalService, vcDataService, commService);
                  loader.setLoadingState.set(false);

                  return false;
                }
                sessionStorage.setItem('FINISHED_GOODS_ID', fgId);

                if (vcDataService.isAddFgMode()) {
                  const ospSiteId =
                    vcDataService
                      .getSupplierSiteAndShip()
                      ?.siteList?.find((s: any) => s.SupplierNumber === requestHeader.supplierSite)
                      ?.SupplierSiteId ??
                    searchResponse.OSPSiteId ??
                    null;
                  searchResponse = {
                    ...searchResponse,
                    ...vcDataService.mapFgDetailsFromRequestHeader(requestHeader),
                    OSPSiteId: ospSiteId,
                    ShipType: vcDataService.resolveShipTypeToEnumValue(searchResponse.ShipType),
                  };
                } else {
                  searchResponse = {
                    ...requestHeader,
                    ...vcDataService.mapFgDetailsDataFromCpq(searchResponse, {
                      ...finishedGood,
                      ...requestHeader,
                    }),
                    // ShipType: vcDataService.resolveShipTypeToEnumValue(searchResponse.ShipType),
                  };
                }

                if (requestHeader.sourceApplicationID === 'SIR' || searchResponse.BOMType === 'RepeatableValueAdd') {
                  searchResponse = { ...searchResponse, BOMEditAllowed: false };
                }

                vcDataService.setFgDetails(searchResponse);

                if (fgId) {
                  globalService.triggerPendoIdentify(false, {
                    loginUserId: requestHeader.loginUserId,
                    BusinessUnitName: requestHeader.businessUnit,
                  });
                  router.navigate(['vas-configurator', fgId]);
                }
              }

              return false;
            }),

            catchError(() => {
              const epl = vcDataService.getResponseHeader('ERROR', requestHeader);
              commService.sendToHost({
                type: 'VAS_ERROR',
                payload: epl,
              });
              loader.setLoadingState.set(false);
              return throwError(() => new Error('Unexpected Error'));
            }),
          );
        }),
      );
    }),
  );
};

const errorDialog = (
  requestHeader: any,
  msg: string,
  modalService: ModalService,
  vcDataService: VcDataService,
  commService: IframeCommunication,
) => {
  const config = {
    message: msg,
    icon: 'i-alert-n',
    btnTxt2: 'cmn.ok',
    showIcon: true,
    showCheckBox: false,
    checkBoxMsg: '',
  };
  const dialog = modalService.openCustomWarningDialog(config).subscribe((res) => {
    if (res.action === 'CONFIRM') {
      const pl = vcDataService.getResponseHeader('CANCEL', requestHeader);
      commService.sendToHost({ type: 'CANCEL', payload: pl });
      dialog.unsubscribe();
    }
  });
};
